import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import type { AuthState } from '@/common/types/auth.types';

const baseURL = import.meta.env.VITE_API_EDMA_BASE_URL;

// ============================================================================
// Types
// ============================================================================

export interface PhotoPresignRequest {
  photo_uuid: string;
  assessment_uuid: string;
  mime_type: string;
  file_size: number;
}

export interface PhotoPresignResponse {
  presigned_url: string;
  s3_bucket: string;
  s3_key: string;
  expires_in: number;
}

export interface PhotoConfirmRequest {
  photo_uuid: string;
  assessment_uuid: string;
  s3_key: string;
  s3_bucket: string;
  file_size: number;
  mime_type: string;
  taken_at: string;
  created_by: string;
  category?: string;
  caption?: string;
  latitude?: string;
  longitude?: string;
  width?: number;
  height?: number;
}

export interface PhotoRecord {
  id: number;
  photo_uuid: string;
  assessment_id: number;
  parcel_id: number;
  s3_bucket: string;
  s3_key: string;
  category: string;
  caption: string | null;
  mime_type: string;
  file_size: number;
  width: number | null;
  height: number | null;
  latitude: string | null;
  longitude: string | null;
  taken_at: string;
  uploaded_at: string;
  sync_status: string;
  created_at: string;
  download_url: string | null;
}

export interface PhotoListResponse {
  count: number;
  dataset: PhotoRecord[];
}

// Local photo type (before upload)
export interface LocalPhoto {
  id: string;
  uri: string; // base64 data URL
  timestamp: string;
  caption?: string;
  mimeType?: string;
  fileSize?: number;
  width?: number;
  height?: number;
}

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Generate a unique photo UUID
 */
export function generatePhotoUuid(): string {
  return `photo-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;
}

/**
 * Convert base64 data URL to Blob for S3 upload
 */
export function dataUrlToBlob(dataUrl: string): Blob {
  const [header, base64Data] = dataUrl.split(',');
  const mimeMatch = header.match(/:(.*?);/);
  const mimeType = mimeMatch ? mimeMatch[1] : 'image/jpeg';

  const byteCharacters = atob(base64Data);
  const byteNumbers = new Array(byteCharacters.length);
  for (let i = 0; i < byteCharacters.length; i++) {
    byteNumbers[i] = byteCharacters.charCodeAt(i);
  }
  const byteArray = new Uint8Array(byteNumbers);

  return new Blob([byteArray], { type: mimeType });
}

/**
 * Get MIME type from data URL
 */
export function getMimeTypeFromDataUrl(dataUrl: string): string {
  const match = dataUrl.match(/data:([^;]+);/);
  return match ? match[1] : 'image/jpeg';
}

/**
 * Get file size from base64 data URL (approximate)
 */
export function getFileSizeFromDataUrl(dataUrl: string): number {
  const base64 = dataUrl.split(',')[1];
  if (!base64) return 0;
  // Base64 encodes 3 bytes into 4 characters, so multiply by 0.75
  return Math.round((base64.length * 3) / 4);
}

/**
 * Upload a blob directly to S3 using presigned URL
 */
export async function uploadToS3(
  presignedUrl: string,
  blob: Blob,
  mimeType: string
): Promise<{ success: boolean; error?: string }> {
  try {
    const response = await fetch(presignedUrl, {
      method: 'PUT',
      headers: {
        'Content-Type': mimeType,
      },
      body: blob,
    });

    if (!response.ok) {
      return {
        success: false,
        error: `S3 upload failed: ${response.status}`,
      };
    }

    return { success: true };
  } catch (error) {
    return {
      success: false,
      error: error instanceof Error ? error.message : 'S3 upload failed',
    };
  }
}

/**
 * Full photo upload flow: presign → S3 → confirm
 */
export async function uploadPhoto(
  photo: LocalPhoto,
  assessmentUuid: string,
  userId: string,
  token: string
): Promise<{ success: boolean; error?: string; photoRecord?: PhotoRecord }> {
  const apiBaseUrl = `${baseURL}/api/earthquake`;

  try {
    const mimeType = photo.mimeType || getMimeTypeFromDataUrl(photo.uri);
    const fileSize = photo.fileSize || getFileSizeFromDataUrl(photo.uri);

    // Step 1: Get presigned URL
    const presignResponse = await fetch(`${apiBaseUrl}/photos/presign`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: token,
      },
      body: JSON.stringify({
        photo_uuid: photo.id,
        assessment_uuid: assessmentUuid,
        mime_type: mimeType,
        file_size: fileSize,
      }),
    });

    if (!presignResponse.ok) {
      const errorText = await presignResponse.text();
      return {
        success: false,
        error: `Presign failed: ${presignResponse.status} - ${errorText}`,
      };
    }

    const presignData: PhotoPresignResponse = await presignResponse.json();

    // Step 2: Upload to S3
    const blob = dataUrlToBlob(photo.uri);
    const s3Result = await uploadToS3(presignData.presigned_url, blob, mimeType);

    if (!s3Result.success) {
      return s3Result;
    }

    // Step 3: Confirm upload
    const confirmPayload: PhotoConfirmRequest = {
      photo_uuid: photo.id,
      assessment_uuid: assessmentUuid,
      s3_key: presignData.s3_key,
      s3_bucket: presignData.s3_bucket,
      file_size: fileSize,
      mime_type: mimeType,
      taken_at: photo.timestamp,
      created_by: userId,
      category: 'OTHER',
      caption: photo.caption,
      width: photo.width,
      height: photo.height,
    };

    const confirmResponse = await fetch(`${apiBaseUrl}/photos/confirm`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: token,
      },
      body: JSON.stringify(confirmPayload),
    });

    if (!confirmResponse.ok) {
      const errorText = await confirmResponse.text();
      // Check for duplicate (409) - photo already exists, treat as success
      if (confirmResponse.status === 409) {
        return { success: true };
      }
      return {
        success: false,
        error: `Confirm failed: ${confirmResponse.status} - ${errorText}`,
      };
    }

    const photoRecord: PhotoRecord = await confirmResponse.json();
    return { success: true, photoRecord };
  } catch (error) {
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Photo upload failed',
    };
  }
}

// ============================================================================
// RTK Query API
// ============================================================================

export const photosApi = createApi({
  reducerPath: 'photosApi',
  baseQuery: fetchBaseQuery({
    baseUrl: `${baseURL}/api/earthquake`,
    prepareHeaders: (headers, { getState }) => {
      const token = (getState() as AuthState).auth.tokenVerification?.accessToken;
      if (token) {
        headers.set('Authorization', token);
      }
      headers.set('Content-Type', 'application/json');
      return headers;
    },
  }),
  tagTypes: ['Photo', 'PhotoList'],
  endpoints: (builder) => ({
    /**
     * Get photos for an assessment
     */
    getPhotosForAssessment: builder.query<PhotoListResponse, string>({
      query: (assessmentUuid) => ({
        url: `/photos?assessment_uuid=${assessmentUuid}`,
        method: 'GET',
      }),
      providesTags: (_result, _error, assessmentUuid) => [
        { type: 'PhotoList', id: assessmentUuid },
      ],
    }),

    /**
     * Delete a photo
     */
    deletePhoto: builder.mutation<{ success: boolean; photo_uuid: string }, string>({
      query: (photoUuid) => ({
        url: `/photos/${photoUuid}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['PhotoList'],
    }),
  }),
});

export const { useGetPhotosForAssessmentQuery, useDeletePhotoMutation } = photosApi;
